/**
 * As partes do formulário do crédito (E207.F7.C01, owner, 01/11): o resultado da linha, os valores
 * lidos (recolhidos na linha da conferência atrás de "Corrigir um valor lido", abertos fora dela,
 * com o alvo quando há mais de um crédito lido) e a frase do que falta para salvar. O estado e a
 * gravação ficam em `FormularioDoCredito` e `correcaoDoCredito`.
 */
import { Button, Field, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  RESULTADOS_DA_LINHA,
  vigenteEntre,
  type CampoDoCreditoLido,
  type CreditoNoPainelData,
  type ResultadoDaLinha,
} from '@/lib/revisaoDoDado';

import { AlvoDoCredito } from './AlvoDoCredito';
import { CamposDoCredito } from './CamposDoCredito';
import type { EdicaoDoAlvo } from './correcaoDoCredito';
import type { Mesa } from './tipos';

const t = ptBR.painelDoDado;

export function Resultado({
  resultado,
  onResultado,
}: {
  resultado: ResultadoDaLinha | '';
  onResultado: (resultado: ResultadoDaLinha | '') => void;
}): JSX.Element {
  return (
    <Field label={t.linha.rotulo} htmlFor="painel-credito-resultado">
      <Select
        id="painel-credito-resultado"
        value={resultado}
        onChange={(e) => onResultado(vigenteEntre(RESULTADOS_DA_LINHA, e.target.value))}
        data-testid="painel-credito-resultado"
      >
        <option value="" disabled>
          {t.linha.escolha}
        </option>
        {RESULTADOS_DA_LINHA.map((r) => (
          <option key={r} value={r}>
            {t.linha.resultados[r]}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** O alvo (quando há mais de um crédito lido) e os campos dele; sem alvo, pede a escolha. */
function ValoresDoAlvo({
  creditos,
  edicao,
  campos,
  mesa,
}: {
  creditos: readonly CreditoNoPainelData[];
  edicao: EdicaoDoAlvo;
  campos: readonly CampoDoCreditoLido[];
  mesa: Mesa;
}): JSX.Element {
  const { alvo, atual } = edicao;
  return (
    <>
      {creditos.length > 1 ? (
        <AlvoDoCredito
          creditos={creditos}
          mesa={mesa}
          escolhido={edicao.escolhido}
          onEscolher={edicao.setEscolhido}
        />
      ) : null}
      {alvo === undefined || atual === null ? (
        <p className="text-body-sm text-fg-muted" data-testid="painel-credito-escolha-antes">
          {t.credito.escolhaAntes}
        </p>
      ) : (
        <CamposDoCredito
          alvo={alvo}
          campos={campos}
          valores={atual.valores}
          onValores={(valores) => edicao.setEdicao({ ...atual, valores })}
          fonte={atual.fonte}
          onFonte={(fonte) => edicao.setEdicao({ ...atual, fonte })}
          mesa={mesa}
        />
      )}
    </>
  );
}

/** Os valores lidos: recolhidos na linha da conferência, abertos fora dela. */
export function Correcao({
  creditos,
  peloResultado,
  corrigindo,
  onCorrigindo,
  edicao,
  campos,
  mesa,
}: {
  creditos: readonly CreditoNoPainelData[];
  peloResultado: boolean;
  corrigindo: boolean;
  onCorrigindo: () => void;
  edicao: EdicaoDoAlvo;
  campos: readonly CampoDoCreditoLido[];
  mesa: Mesa;
}): JSX.Element {
  if (creditos.length === 0) {
    return (
      <p className="text-body-sm text-fg-muted" data-testid="painel-credito-sem">
        {t.credito.semCredito}
      </p>
    );
  }
  return (
    <>
      {peloResultado ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          aria-expanded={corrigindo}
          onClick={onCorrigindo}
          data-testid="painel-credito-corrigir"
        >
          {corrigindo ? t.credito.manterValoresLidos : t.credito.corrigirValorLido}
        </Button>
      ) : null}
      {corrigindo ? (
        <ValoresDoAlvo creditos={creditos} edicao={edicao} campos={campos} mesa={mesa} />
      ) : null}
    </>
  );
}
